/**
 * Google reCAPTCHA v3 token minting.
 *
 * The script is injected lazily on first use (so it costs nothing on pages that never
 * submit), and everything no-ops when REACT_APP_RECAPTCHA_SITE_KEY is unset — which keeps
 * the app working exactly as before until the client's keys are installed.
 *
 * v3 tokens are single-use and short-lived, so a fresh one is minted per submission.
 */

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const SCRIPT_ID = "recaptcha-v3";

let scriptPromise = null;

const loadScript = () => {
  if (!SITE_KEY) return Promise.resolve(false);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve) => {
    if (document.getElementById(SCRIPT_ID) || window.grecaptcha) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false); // never block the app on a load failure
    document.head.appendChild(script);
  });

  return scriptPromise;
};

/**
 * Inject the reCAPTCHA script up front. Call this when a page that can submit a protected
 * form mounts, so Google's "protected by reCAPTCHA" badge is visible before the user
 * submits — v3 has no checkbox, so that badge is the only visible sign it is active.
 */
export const loadRecaptchaScript = () => loadScript();

/**
 * @param {string} action a label for this submission, shown in Google's admin console
 * @returns {Promise<string|null>} the token, or null when unconfigured/unavailable
 */
export const getRecaptchaToken = async (action = "submit") => {
  if (!SITE_KEY) return null;

  const loaded = await loadScript();
  if (!loaded || !window.grecaptcha?.execute) return null;

  try {
    await new Promise((resolve) => window.grecaptcha.ready(resolve));
    return await window.grecaptcha.execute(SITE_KEY, { action });
  } catch (error) {
    console.log("reCAPTCHA token error", error);
    return null;
  }
};

/** Paths that the backend challenges — keep in step with the `recaptcha` middleware. */
export const needsRecaptcha = (url = "", method = "get") => {
  if (!SITE_KEY) return false;
  if (["get", "head", "options"].includes(String(method).toLowerCase())) return false;

  const path = String(url);

  return (
    path.includes("login") ||
    path.includes("forgot-password") ||
    path.includes("reset-password") ||
    path.includes("validate-reset-token") ||
    path.includes("/interpreter/") ||
    path.startsWith("interpreter/")
  );
};

export default getRecaptchaToken;
